import { EventListener, getComponentByDomNode, GondelBaseComponent } from '@gondel/core';
import { ArrayLikeHtmlElement } from '@gondel/core/dist/GondelDomUtils';
import { SearchInput, SearchInputEvents } from '../../../patterns/molecules/search-input/js/search-input';
import sanitize from 'sanitize-html';

export interface ISearchableOverviewSelectors {
	Button: string;
	ButtonContainer: string;
	StoriesContainer: string;
	SearchInput: string;
}

export interface ISearchableOverviewStates {
	ButtonInvisible: string;
}

export class StoryModel {
	public rendered: string = "";
}

enum AssociatedParams {
	Search = 'search',
	Pagination = 'page',
}

enum CustomDataAttributes {
	NumOfResults = 'data-num-of-results',
	PageApiUrl = 'data-page-api',
}

export class SearchableOveriewComponent extends GondelBaseComponent {
	private _selectors: ISearchableOverviewSelectors | undefined;
	private _states: ISearchableOverviewStates | undefined;

	private _currentPage = -1;
	private _numOfTotalResults = 0;

	private _pageApi: string = "";

	private _currentPageSearchParams: URLSearchParams | undefined;

	private _storiesContainer: HTMLElement = {} as HTMLElement;
	private _loadMoreButton: HTMLElement = {} as HTMLElement;

	private _storyWrapperClasses: string[] = [];

	/**
	 * Sets up the whole searching logic.
	 * @param selectors Defines all needed selectors for searching logic
	 * @param states
	 */
	public start(
		selectors: ISearchableOverviewSelectors,
		states: ISearchableOverviewStates,
		storyWrapperClasses: string[]
	) {
		this._selectors = selectors;
		this._states = states;
		this._storyWrapperClasses = storyWrapperClasses;

		this._storiesContainer = this._ctx.querySelector(this._selectors.StoriesContainer) as HTMLElement;

		this._pageApi = this._ctx.getAttribute(CustomDataAttributes.PageApiUrl) ?? '';
		this._numOfTotalResults = Number(this._ctx.getAttribute(CustomDataAttributes.NumOfResults) ?? 0);

		this._ctx.removeAttribute(CustomDataAttributes.PageApiUrl);
		this._ctx.removeAttribute(CustomDataAttributes.NumOfResults);

		this._currentPageSearchParams = new URLSearchParams(window.location.search);

		this._hideButtonOnReachedItemsCount();
		this._populateSearchBox();
		this._setupListeners();
	}

	public stop() {
		this._loadMoreButton.removeEventListener('click', () => this._onLoadClick());
	}

	private _setupListeners() {
		// Load more
		if (this._selectors?.Button) {
			this._loadMoreButton = this._ctx.querySelector(this._selectors.Button) as HTMLElement;
			this._loadMoreButton.addEventListener('click', () => this._onLoadClick());
		}
	}

	/* eslint-disable */
	// @ts-ignore
	@EventListener(SearchInputEvents.OnSearchClick)
	private _onSearch(event: any): void {
		const currentLocation = window.location.origin.concat(window.location.pathname);
		window.open(currentLocation.concat('?', AssociatedParams.Search, '=', event.data.eventData.inputText), '_self');
	}
	/* eslint-enable */

	private _onLoadClick() {
		this._currentPage += 1;

		let loadUrl = this._pageApi.concat('?', AssociatedParams.Pagination, '=', this._currentPage.toString());

		// Append search parameter to pagination url so only searched results are loaded afterwards
		if (window.location.search.includes(AssociatedParams.Search)) {
			const sp = this._currentPageSearchParams 
				? this._currentPageSearchParams.get(AssociatedParams.Search) ?? ''
				: '';
			loadUrl = loadUrl.concat(
				'&',
				AssociatedParams.Search,
				'=',
				sp
			);
		}

		fetch(loadUrl)
			.then(async (response) => response.json())
			.then((result: StoryModel[]) => {
				result.forEach((storyItem: StoryModel) => this._appendStoryItemsToView(storyItem));
				this._hideButtonOnReachedItemsCount();
			});
	}

	private _hideButtonOnReachedItemsCount() {
		if (
			this._storiesContainer.children.length >= this._numOfTotalResults 
			&& this._selectors 
			&& this._states
		) {
			const buttonContainer = this._ctx.querySelector(this._selectors.ButtonContainer);
			buttonContainer?.classList.add(this._states.ButtonInvisible);
		}
	}

	private _appendStoryItemsToView(storyItem: StoryModel) {
		const storyElementWrapper = document.createElement('div');

		storyElementWrapper.classList.add(...this._storyWrapperClasses);
		// bearer:disable javascript_lang_dangerous_insert_html
		storyElementWrapper.innerHTML = storyItem.rendered;

		this._storiesContainer.append(storyElementWrapper);
	}

	/**
	 * Populate search box after the user has searched
	 * @private
	 */
	private _populateSearchBox() {
		if (!this._currentPageSearchParams) return;
		const searchParamValue = this._currentPageSearchParams.get(AssociatedParams.Search);

		if (searchParamValue && this._selectors) {
			const searchInput: ArrayLikeHtmlElement = this._ctx.querySelector(
				this._selectors.SearchInput
			) as ArrayLikeHtmlElement;
			const searchInputComponent = getComponentByDomNode<SearchInput>(searchInput);

			searchInputComponent.text = searchParamValue;
		}
	}
}
